import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useFormik } from 'formik'
import { Modal } from 'components/core-ui/Modal'
import { useNotification } from 'hooks'
import {
  useCreateDelegateMutation,
  useDelegateQuery,
  useDelegatesQuery,
  useUpdateDelegateMutation
} from '../../api'
import { DelegateTableItem, EditDelegateModalProps, ManagersTableItem } from '../../interfaces'
import { delegateFromListValidationSchema, delegateValidationSchema } from './helpers'
import { NewDelegateForm } from './components/DelegateForm'
import { Box, Grid } from '@mui/material'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import { Button, IconButton, Input } from '@fullscreendigital/carrefour-dex-fe'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { GroupsOutlined } from '@mui/icons-material'

const AddDelegateToManagerModal: React.FC<EditDelegateModalProps> = ({
  isModalOpen = false,
  onModalClose,
  manager,
  delegateId,
  setShouldRefreshData
}) => {
  const { showNotification } = useNotification()
  const shouldRefreshData = useMemo(() => isModalOpen === true, [isModalOpen])
  const [showNewDelegateForm, setShowNewDelegateForm] = useState<boolean>(false)
  const [searchDelegateText, setSearchDelegateText] = useState<string>('')
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>('')
  const [delegatesOptions, setDelegatesOptions] = useState([])

  const { data: delegateData, isLoading: delegateLoading } = useDelegateQuery(
    showNotification,
    delegateId,
    shouldRefreshData
  )
  const { data: delegatesListData, isLoading: searchLoading } = useDelegatesQuery(
    showNotification,
    toTableGETUrl({
      url: '/iam/external-partners/delegates',
      searchText: debouncedSearchText.split(' ')[0] || '',
      searchFields: ['firstName', 'lastName', 'email'],
      tableFilters: [{ name: 'isEnabled', value: true }],
      page: 0,
      rowsPerPage: 1000
    }),
    { enabled: isModalOpen }
  )

  useEffect(
    () => setDelegatesOptions(getDelegateOptions(delegatesListData?.list || [])),
    [delegatesListData]
  )

  const updateDelegateMutation = useUpdateDelegateMutation(showNotification)
  const createDelegateMutation = useCreateDelegateMutation(showNotification)

  const isLoading = delegateLoading || searchLoading

  const showNewDelegateHandler = () => {
    if (showNewDelegateForm) {
      formik.setFieldValue('id', null)
    } else {
      formik.resetForm()
    }
    setShowNewDelegateForm(!showNewDelegateForm)
  }

  const saveDelegate = async (values: Partial<DelegateTableItem>) => {
    try {
      if (!showNewDelegateForm) {
        if (!manager || values.id == null) {
          return
        }
        const currentDelegate = delegatesListData?.list?.find(d => d.id === values.id)
        const updatedManager = { ...manager, delegates: [] }
        const updatedManagers = [...currentDelegate.managers, updatedManager]
        await updateDelegateMutation.mutateAsync({
          managerId: manager.id,
          delegateId: values.id,
          updatedData: { id: currentDelegate.id, managers: updatedManagers }
        })
      } else if (!delegateId) {
        await createDelegateMutation.mutateAsync(values)
      } else {
        if (!manager) {
          return
        }
        values.id = delegateId
        await updateDelegateMutation.mutateAsync({
          managerId: manager.id,
          delegateId,
          updatedData: values
        })
      }
      setShouldRefreshData()
      setSearchDelegateText('')
      formik.resetForm()
      onModalClose()
    } catch (error) {
      // no need to handle error here
    }
  }

  const handleClose = () => {
    onModalClose()
    formik.resetForm()
    setSearchDelegateText('')
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: delegateData?.id || null,
      delegate: delegateData || null,
      firstName: delegateData?.firstName || '',
      lastName: delegateData?.lastName || '',
      email: delegateData?.email || '',
      phone: delegateData?.phone || '',
      managers: [{ id: manager?.id }] as Partial<ManagersTableItem[]>,
      isEnabled: true
    },
    validationSchema: showNewDelegateForm
      ? delegateValidationSchema
      : delegateFromListValidationSchema,
    onSubmit: values => saveDelegate(values)
  })

  const getDelegateOptions = useCallback(
    (delegateDetails: DelegateTableItem[]) => {
      return (
        delegateDetails?.map(d => ({
          ...d,
          label: `${d.firstName} ${d.lastName}`,
          value: d.id
        })) || []
      )
    },
    [manager, delegatesListData]
  )

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchDelegateText)
    }, 300)

    return () => {
      clearTimeout(handler)
    }
  }, [searchDelegateText])

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={handleClose}
      modalTitle={'Assign delegate'}
      modalWidth="50%"
    >
      <Grid container spacing={2}>
        {showNewDelegateForm ? (
          <NewDelegateForm formik={formik} />
        ) : (
          <Grid item xs={8}>
            <AsyncAutocomplete
              variant="outlined"
              name="id"
              label="Choose an existing delegate"
              options={delegatesOptions || []}
              onChange={val => {
                formik.setFieldValue('id', val?.value)
                formik.setFieldValue('delegate', val)
              }}
              // onInputChange={handlerSearchDelegate}
              error={formik.touched.id && Boolean(formik.errors.id)}
              helperText={formik.touched.id && formik.errors.id}
            />
          </Grid>
        )}
        {showNewDelegateForm ? (
          <Grid item xs={12}>
            <Box>
              <IconButton
                variant="outlined"
                onClick={showNewDelegateHandler}
                startIcon={<GroupsOutlined />}
                label={'Choose delegate from list'}
              />
            </Box>
          </Grid>
        ) : (
          <Grid item xs={4}>
            <Box style={{ marginTop: '20px', textAlign: 'right' }}>
              <IconButton
                variant="outlined"
                onClick={showNewDelegateHandler}
                startIcon={<GroupsOutlined />}
                label={'New delegate'}
              />
            </Box>
          </Grid>
        )}
        {!showNewDelegateForm && (
          <>
            <Grid item xs={12}>
              <Input
                type="text"
                variant="outlined"
                name="firstName"
                label="First Name"
                width="100%"
                value={formik.values.delegate?.firstName}
                required
                disabled
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                type="text"
                variant="outlined"
                name="lastName"
                label="Last Name"
                width="100%"
                value={formik.values.delegate?.lastName}
                required
                disabled
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                type="text"
                variant="outlined"
                name="email"
                label="Email"
                width="100%"
                value={formik.values.delegate?.email}
                required
                disabled
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                type="text"
                variant="outlined"
                name="phone"
                label="Phone"
                width="100%"
                value={formik.values.delegate?.phone}
                required
                disabled
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} className={s.modalButtons}>
          <Button variant="outlined" onClick={handleClose} label={'Cancel'} />
          <Button
            variant="contained"
            label={'Save'}
            onClick={() => formik.handleSubmit()}
            disabled={formik.isSubmitting}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default AddDelegateToManagerModal
